import { useArticles } from "@faire/help-center-utils/hooks/article/useArticles";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Application } from "@faire/web--source/slate/icons/foundation/Application";
import React from "react";

import { SelfServeNodes } from "../../types";
import { AddPaymentMethodIA } from "../instantAnswers/invoicesAndPayments-addPaymentMethod";
import { BulkDownloadIA } from "../instantAnswers/invoicesAndPayments-bulkDownload";
import { CanceledItemsIA } from "../instantAnswers/invoicesAndPayments-canceledItems";
import { ChangeDefaultPaymentMethodIA } from "../instantAnswers/invoicesAndPayments-changeDefaultPaymentMethod";
import { InvoiceBasicsIA } from "../instantAnswers/invoicesAndPayments-invoiceBasics";
import { ManageBillToAddressIA } from "../instantAnswers/invoicesAndPayments-manageBillToAddress";
import { Net60BasicsIA } from "../instantAnswers/invoicesAndPayments-net60Basics";
import { PaymentProcessingErrorIA } from "../instantAnswers/invoicesAndPayments-paymentProcessingError";
import { RefundsIA } from "../instantAnswers/invoicesAndPayments-refunds";
import { ReplacementItemsIA } from "../instantAnswers/invoicesAndPayments-replacementItems";
import { ReportDamagedMissingItemsOrShipmentsIA } from "../instantAnswers/invoicesAndPayments-reportDamagedMissingItemsOrShipments";
import { RequestCreditLimitIncreaseIA } from "../instantAnswers/invoicesAndPayments-requestCreditLimitIncrease";
import { ViewOrUpdatePaymentMethodsIA } from "../instantAnswers/invoicesAndPayments-viewOrUpdatePaymentMethods";
import { WhereToFindInvoicesL3IA } from "../instantAnswers/invoicesAndPayments-whereToFindInvoicesL3";
import { BillingAddressIA } from "../instantAnswers/shared-billingAddress";

export const useInvoicesAndPaymentsRetailerNodes = (): SelfServeNodes => {
  const { strictLocalize } = useStrictLocalization();
  const articles = useArticles();
  return {
    InvoicesAndPayments: {
      id: "InvoicesAndPayments",
      label: strictLocalize({
        defaultMessage: "Invoices & payments",
        description: {
          text: "A top level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      icon: <Application fontSize="24px" color="faireBlack" />,
      children: [
        "WhereToFindInvoices",
        "InvoiceBasics",
        "ManageBillToAddress",
        "DamagedOrMissingItemChanges",
        "NetTerms",
        "PaymentMethods",
        "PaymentProcessingError",
        "Refunds",
      ],
      level: 1,
      sidebarTitle: strictLocalize({
        defaultMessage: "Invoice or payment issue",
        description: {
          text: "Title for displaying the issue on the order summary sidebar",
        },
      }),
    },
    WhereToFindInvoices: {
      id: "WhereToFindInvoices",
      label: strictLocalize({
        defaultMessage: "Where to find invoices",
      }),
      children: ["WhereToFindInvoicesL3", "BulkDownload"],
      level: 2,
    },
    InvoiceBasics: {
      id: "InvoiceBasics",
      label: strictLocalize({
        defaultMessage: "Invoice basics",
      }),
      instantAnswer: InvoiceBasicsIA(strictLocalize),
      articles: [articles.PaymentsAndInvoices.UNDERSTANDING_ORDER_INVOICE],
      level: 2,
    },
    ManageBillToAddress: {
      id: "ManageBillToAddress",
      label: strictLocalize({
        defaultMessage: "Manage “Bill to” address",
      }),
      instantAnswer: ManageBillToAddressIA(strictLocalize),
      articles: [
        articles.PaymentsAndInvoices.HOW_DO_I_CHANGE_MY_BILLING_ADDRESS,
      ],
      level: 2,
    },
    DamagedOrMissingItemChanges: {
      id: "DamagedOrMissingItemChanges",
      label: strictLocalize({
        defaultMessage: "Damaged or missing item charges",
      }),
      children: [
        "ReplacementItems",
        "CanceledItems",
        "ReportDamagedMissingItemsOrShipments",
      ],
      level: 2,
    },
    NetTerms: {
      id: "NetTerms",
      label: strictLocalize({
        defaultMessage: "Net terms",
      }),
      children: ["Net60Basics", "RequestCreditLimitIncrease"],
      level: 2,
    },
    PaymentMethods: {
      id: "PaymentMethods",
      label: strictLocalize({
        defaultMessage: "Payment methods",
      }),
      children: [
        "AddPaymentMethod",
        "ChangeDefaultPaymentMethod",
        "ViewOrUpdatePaymentMethods",
        "UpdateBillingAddress",
      ],
      level: 2,
    },
    PaymentProcessingError: {
      id: "PaymentProcessingError",
      label: strictLocalize({
        defaultMessage: "Payment processing error",
      }),
      instantAnswer: PaymentProcessingErrorIA(strictLocalize),
      level: 2,
    },
    Refunds: {
      id: "Refunds",
      label: strictLocalize({
        defaultMessage: "Refunds",
      }),
      instantAnswer: RefundsIA(strictLocalize),
      articles: [articles.ReturnsAndRefunds.MAKE_A_RETURN],
      level: 2,
    },
    WhereToFindInvoicesL3: {
      id: "WhereToFindInvoicesL3",
      label: strictLocalize({
        defaultMessage: "Where to find invoices",
      }),
      instantAnswer: WhereToFindInvoicesL3IA(strictLocalize),
      articles: [articles.PaymentsAndInvoices.UNDERSTANDING_ORDER_INVOICE],
      level: 3,
    },
    BulkDownload: {
      id: "BulkDownload",
      label: strictLocalize({
        defaultMessage: "Bulk download",
      }),
      instantAnswer: BulkDownloadIA(strictLocalize),
      articles: [articles.PaymentsAndInvoices.UNDERSTANDING_ORDER_INVOICE],
      level: 3,
    },
    ReplacementItems: {
      id: "ReplacementItems",
      label: strictLocalize({
        defaultMessage: "Replacement item(s)",
      }),
      instantAnswer: ReplacementItemsIA(strictLocalize),
      articles: [articles.MyOrders.REPORT_DAMAGED_MISSING],
      level: 3,
    },
    CanceledItems: {
      id: "CanceledItems",
      label: strictLocalize({
        defaultMessage: "Cancelled item(s)",
      }),
      instantAnswer: CanceledItemsIA(strictLocalize),
      articles: [articles.MyOrders.REPORT_DAMAGED_MISSING],
      level: 3,
    },
    ReportDamagedMissingItemsOrShipments: {
      id: "ReportDamagedMissingItemsOrShipments",
      label: strictLocalize({
        defaultMessage: "Report damaged, missing item(s) or shipment(s)",
      }),
      instantAnswer: ReportDamagedMissingItemsOrShipmentsIA(strictLocalize),
      level: 3,
    },
    Net60Basics: {
      id: "Net60Basics",
      label: strictLocalize({
        defaultMessage: "Net60 basics",
      }),
      instantAnswer: Net60BasicsIA(strictLocalize),
      articles: [articles.PaymentsAndInvoices.NET_60_TERMS],
      level: 3,
    },
    RequestCreditLimitIncrease: {
      id: "RequestCreditLimitIncrease",
      label: strictLocalize({
        defaultMessage: "Request credit limit increase",
      }),
      instantAnswer: RequestCreditLimitIncreaseIA(strictLocalize),
      articles: [articles.PaymentsAndInvoices.NET_60_TERMS?.atSection("2")],
      level: 3,
    },
    AddPaymentMethod: {
      id: "AddPaymentMethod",
      label: strictLocalize({
        defaultMessage: "Add payment method",
      }),
      instantAnswer: AddPaymentMethodIA(strictLocalize),
      articles: [
        articles.PaymentsAndInvoices.HOW_DO_I_PAY_FOR_ORDERS,
        articles.PaymentsAndInvoices.CHANGE_MY_PAYMENT_METHOD,
      ],
      level: 3,
    },
    ChangeDefaultPaymentMethod: {
      id: "ChangeDefaultPaymentMethod",
      label: strictLocalize({
        defaultMessage: "Change default payment method",
      }),
      instantAnswer: ChangeDefaultPaymentMethodIA(strictLocalize),
      articles: [articles.PaymentsAndInvoices.CHANGE_MY_PAYMENT_METHOD],
      level: 3,
    },
    ViewOrUpdatePaymentMethods: {
      id: "ViewOrUpdatePaymentMethods",
      label: strictLocalize({
        defaultMessage: "View or update payment methods",
      }),
      instantAnswer: ViewOrUpdatePaymentMethodsIA(strictLocalize),
      articles: [
        articles.PaymentsAndInvoices.HOW_DO_I_PAY_FOR_ORDERS,
        articles.PaymentsAndInvoices.CHANGE_MY_PAYMENT_METHOD,
      ],
      level: 3,
    },
    UpdateBillingAddress: {
      id: "UpdateBillingAddress",
      label: strictLocalize({
        defaultMessage: "Update billing address",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [
        articles.PaymentsAndInvoices.HOW_DO_I_CHANGE_MY_BILLING_ADDRESS,
      ],
      instantAnswer: BillingAddressIA(strictLocalize),
      level: 3,
    },
  };
};
